import React, {memo, useEffect, useRef} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '../display-image';
import {useRovingTabs} from '@/hooks/useRovingTabs';
import {
    borderRadius,
    borderWidth,
    colors,
    contentMaxWidth,
    fontSize,
    fontWeight,
    lineHeightRatio,
    overlay,
    shadows,
    transition
} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

export interface ComplianceTabBadge {
    name: string;
    /** Badge artwork. Omitted where no official mark exists; a neutral shield stands in. */
    icon?: string;
}

export interface ComplianceTabGroup {
    title: string;
    badges: ComplianceTabBadge[];
}

export interface ComplianceTabsProps {
    heading: string;
    subheading: string;
    groups: ComplianceTabGroup[];
    /** On a white section: dark heading, navy-outlined tabs and bordered badge tiles. Default is the navy band. */
    light?: boolean;
}

const sectionSx = {bgcolor: colors.primary.deep} as const;

const headerWrapSx = {textAlign: 'center', maxWidth: contentMaxWidth.wide, mx: 'auto', mb: {xs: 3, md: 3}} as const;

const headingSx = [sectionHeadingSx, {color: colors.text.inverse, mb: 1.5, textWrap: 'balance'}] as const;

const subheadingSx = {
    color: colors.text.inverse,
    lineHeight: lineHeightRatio.looser,
    maxWidth: contentMaxWidth.standard,
    mx: 'auto'
} as const;

const panelSx = {
    border: `${borderWidth.thin} solid ${overlay.whiteLight}`,
    borderRadius: borderRadius['2xl'],
    p: {xs: 2, md: 3},
    pt: {xs: 2, md: 2.5}
} as const;

// Phones: one swipeable row of chips (no scrollbar). From sm: the chips wrap and centre.
const tabListSx = {
    display: 'flex',
    flexWrap: {xs: 'nowrap', sm: 'wrap'},
    justifyContent: {xs: 'flex-start', sm: 'center'},
    gap: 1.5,
    mb: {xs: 3, md: 4},
    overflowX: {xs: 'auto', sm: 'visible'},
    scrollSnapType: {xs: 'x proximity', sm: 'none'},
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {display: 'none'},
    // Room for the focus outline, which the scroll box would otherwise clip.
    p: {xs: '4px 2px', sm: 0}
} as const;

const tabBaseSx = {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    scrollSnapAlign: 'start',
    px: 2.5,
    py: 1,
    borderRadius: borderRadius.full,
    border: `${borderWidth.thin} solid ${colors.text.inverse}`,
    fontSize: fontSize[14],
    fontWeight: fontWeight.medium,
    lineHeight: lineHeightRatio.snug,
    cursor: 'pointer',
    transition: `background-color ${transition.base}, color ${transition.base}`,
    '&:focus-visible': {outline: `${borderWidth.thick} solid ${colors.text.inverse}`, outlineOffset: 2}
} as const;

const tabSx = {
    ...tabBaseSx,
    bgcolor: 'transparent',
    color: colors.text.inverse,
    '&:hover': {bgcolor: overlay.whiteTenth}
} as const;

const tabActiveSx = {...tabBaseSx, bgcolor: colors.background.default, color: colors.text.primary} as const;

// ─── `light` ─────────────────────────────────────────────────────────────────
const lightSectionSx = {bgcolor: colors.background.default} as const;
const lightHeadingSx = [sectionHeadingSx, {color: colors.text.primary, mb: 1.5, textWrap: 'balance'}] as const;
const lightSubheadingSx = {...subheadingSx, color: colors.text.primary} as const;
const lightPanelSx = {p: 0} as const;
const lightTabBaseSx = {
    ...tabBaseSx,
    border: `${borderWidth.thin} solid ${colors.primary.deep}`,
    '&:focus-visible': {outline: `${borderWidth.thick} solid ${colors.primary.deep}`, outlineOffset: 2}
} as const;
const lightTabSx = {
    ...lightTabBaseSx,
    bgcolor: 'transparent',
    color: colors.primary.deep,
    '&:hover': {bgcolor: colors.background.tag}
} as const;
const lightTabActiveSx = {...lightTabBaseSx, bgcolor: colors.primary.deep, color: colors.text.inverse} as const;

const badgeRowSx = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 1.5,
    listStyle: 'none',
    m: 0,
    p: 0
} as const;

// Six tiles a row on desktop, three on tablet, two on phones; with `justifyContent: center`
// a short last row sits centred under the full one above it.
const badgeSx = {
    flex: {
        xs: '0 0 calc((100% - 12px) / 2)',
        sm: '0 0 calc((100% - 24px) / 3)',
        md: '0 0 calc((100% - 60px) / 6)'
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    minHeight: 104,
    p: 1.5,
    borderRadius: borderRadius.lg,
    bgcolor: colors.background.default,
    // Hovering the card zooms only its icon (the first child: logo or shield tile); the card and name stay put.
    '&:hover > :first-child': {transform: 'scale(1.15)'},
    '@media (prefers-reduced-motion: reduce)': {
        '&:hover > :first-child': {transform: 'none'}
    }
} as const;

const lightBadgeSx = {
    ...badgeSx,
    border: `${borderWidth.thin} solid ${colors.neutral.borderLight}`,
    boxShadow: shadows.card
} as const;

const badgeLogoSx = {position: 'relative', width: 56, height: 56, transition: `transform ${transition.medium}`} as const;

const badgeFallbackSx = {
    ...badgeLogoSx,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: borderRadius.md,
    bgcolor: colors.background.tag,
    color: colors.primary.deep
} as const;

const fallbackIconSx = {fontSize: fontSize[28]} as const;

const badgeNameSx = {color: colors.text.primary, fontSize: fontSize[14], fontWeight: fontWeight.bold, textAlign: 'center'} as const;

const logoStyle = {objectFit: 'contain'} as const;

const ComplianceBadge = memo(function ComplianceBadge({badge, light}: {badge: ComplianceTabBadge; light?: boolean}) {
    return (
        <Box component="li" sx={light ? lightBadgeSx : badgeSx}>
            {badge.icon ? (
                <Box sx={badgeLogoSx}>
                    <DisplayImage src={badge.icon} alt={`${badge.name} logo`} fill sizes="56px" style={logoStyle} />
                </Box>
            ) : (
                <Box sx={badgeFallbackSx} aria-hidden="true">
                    <ShieldOutlinedIcon sx={fallbackIconSx} />
                </Box>
            )}
            <Typography component="span" className="font-headers" sx={badgeNameSx}>
                {badge.name}
            </Typography>
        </Box>
    );
});

const ComplianceTabs = ({heading, subheading, groups, light = false}: ComplianceTabsProps) => {
    const {active, getTabProps, getPanelProps} = useRovingTabs(groups.length);
    const tabListRef = useRef<HTMLDivElement>(null);

    // When the chip row scrolls (phones), bring the selected chip into view. Scrolls the row only, never the page.
    useEffect(() => {
        const list = tabListRef.current;
        const tab = list?.children[active] as HTMLElement | undefined;
        if (!list || !tab || list.scrollWidth <= list.clientWidth) return;
        const left = tab.offsetLeft - list.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
        list.scrollTo({left, behavior: 'smooth'});
    }, [active]);

    return (
        <Box sx={light ? lightSectionSx : sectionSx}>
            <MUIContainer>
                <Box sx={headerWrapSx}>
                    <Typography variant="h1" component="h2" className="font-headers" sx={light ? lightHeadingSx : headingSx}>
                        {heading}
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        component="p"
                        className="font-regular-text"
                        sx={light ? lightSubheadingSx : subheadingSx}
                    >
                        {subheading}
                    </Typography>
                </Box>

                <Box sx={light ? lightPanelSx : panelSx}>
                    <Box ref={tabListRef} role="tablist" aria-label={heading} sx={tabListSx}>
                        {groups.map((group, index) => {
                            const selected = index === active;
                            return (
                                <Box
                                    key={group.title}
                                    component="button"
                                    {...getTabProps(index)}
                                    className="font-regular-text"
                                    sx={light ? (selected ? lightTabActiveSx : lightTabSx) : selected ? tabActiveSx : tabSx}
                                >
                                    {group.title}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Every panel is server-rendered so all badges are in the HTML; inactive ones are only hidden. */}
                    {groups.map((group, index) => (
                        <Box key={group.title} {...getPanelProps(index)}>
                            <Box component="ul" sx={badgeRowSx}>
                                {group.badges.map((badge) => (
                                    <ComplianceBadge key={badge.name} badge={badge} light={light} />
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(ComplianceTabs);
